import type { Application } from "../types";
import type { IssuePoHeader, IssuePoItem } from "../api/mrrInventoryApi";
import {
  aggregateIssuePoItems,
  buildConstructionPoHtml,
  buildPoAnnexureHtml,
  DEFAULT_PO_DIGITAL_FOOTER,
  detailedLinesFromIssuePoItems,
  getBeneficiaryAllocations,
  type AggregatedPoLine,
  type DetailedPoLine,
  type IssuePoPdfDocument,
} from "./constructionPoDocument";
import { downloadPdfFromHtml, htmlDocumentToPdfBlob, warmPoPdfLogoCache } from "./downloadPdfFromHtml";
import { geoByApplicationIdFromApplications } from "./poBeneficiaryGeo";
import { loadPoFormatSettings } from "./poFormatSettings";
import { poDocumentDate } from "./poDocumentDate";

export type { IssuePoPdfDocument };

type PoDocLineRow = AggregatedPoLine | DetailedPoLine;

function lineSubtitle(ln: PoDocLineRow): string {
  return ln && typeof ln === "object" && "beneficiaryName" in ln ? ln.beneficiaryName : ln.standardResourceTitle;
}

function parseMoney(raw: string): number {
  const n = Number.parseFloat(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

export function buildIssuePoPdfDocument(
  header: IssuePoHeader,
  items: IssuePoItem[],
  options?: {
    rateByKey?: Record<string, string>;
    hsnByKey?: Record<string, string>;
    applications?: Application[];
  },
): IssuePoPdfDocument {
  if (!items.length) throw new Error("No PO lines to export.");

  const s = loadPoFormatSettings();
  const aggregateQty = s.aggregateQtyDefault;
  const baseLines: PoDocLineRow[] = aggregateQty ? aggregateIssuePoItems(items) : detailedLinesFromIssuePoItems(items);

  const rateByKey = options?.rateByKey ?? {};
  const hsnByKey = options?.hsnByKey ?? {};

  const htmlLines = baseLines.map((ln, idx) => {
    const rawOverride = rateByKey[ln.key];
    const rate =
      rawOverride != null && String(rawOverride).trim() !== ""
        ? parseMoney(rawOverride)
        : (ln.rate ?? 0);
    const qty = ln.totalQty;
    const amount = qty * rate;
    const subDesc =
      "beneficiaryName" in ln
        ? `${ln.standardResourceTitle} · Beneficiary: ${lineSubtitle(ln)}`
        : ln.standardResourceTitle;
    return {
      sno: idx + 1,
      description: ln.resourceItemLabel,
      subDescription: subDesc,
      hsn: (hsnByKey[ln.key] ?? "").trim() || (ln.hsnCode ?? "").trim(),
      qty,
      unit: ln.resourceItemUnit,
      rate,
      amount,
    };
  });

  const taxableTotal = htmlLines.reduce((sum, x) => sum + x.amount, 0);
  const gstLines = s.gstRows.map((g) => ({
    label: g.label.trim() || "Tax",
    ratePercent: g.ratePercent,
    amount: taxableTotal * (g.ratePercent / 100),
  }));
  const grandTotal = taxableTotal + gstLines.reduce((sum, x) => sum + x.amount, 0);

  const geoByAppId = options?.applications?.length
    ? geoByApplicationIdFromApplications(options.applications)
    : undefined;
  const allocations = getBeneficiaryAllocations(items, geoByAppId);

  return {
    documentTitle: s.documentTitle,
    letterheadTagline: s.letterheadTagline,
    accentColorHex: s.accentColorHex,
    footerNote: s.footerNote,
    poNumber: header.poNumber,
    poDate: poDocumentDate(header),
    buyerName: s.buyerName,
    buyerAddress: s.buyerAddress,
    buyerGstin: s.buyerGstin,
    vendorName: s.defaultVendorName.trim() || header.subcontractorFirmName,
    vendorAddress: s.defaultVendorAddress,
    vendorGstin: s.defaultVendorGstin,
    shipTo: header.shipToPrintText?.trim() || s.defaultShipTo,
    paymentTerms: s.paymentTerms,
    lines: htmlLines,
    taxableTotal,
    gstLines,
    grandTotal,
    allocations,
  };
}

export function buildIssuePoPdfHtml(
  header: IssuePoHeader,
  items: IssuePoItem[],
  options?: {
    rateByKey?: Record<string, string>;
    hsnByKey?: Record<string, string>;
    applications?: Application[];
    /** True for PDF download — faster capture. False/omit for on-screen iframe preview (web fonts). */
    forPdfCapture?: boolean;
  },
): { html: string; annexureHtml: string; downloadBasename: string } {
  const doc = buildIssuePoPdfDocument(header, items, options);
  const s = loadPoFormatSettings();
  const pdfCap = options?.forPdfCapture === true;

  const html = buildConstructionPoHtml({
    documentTitle: doc.documentTitle,
    letterheadTagline: doc.letterheadTagline,
    logoPrimaryUrl: s.logoPrimaryUrl,
    logoSecondaryUrl: s.logoSecondaryUrl,
    logoPrimaryHeightPx: s.logoPrimaryHeightPx,
    logoSecondaryHeightPx: s.logoSecondaryHeightPx,
    accentColorHex: doc.accentColorHex,
    footerNote: doc.footerNote.trim() || DEFAULT_PO_DIGITAL_FOOTER,
    defaultFooterNote: DEFAULT_PO_DIGITAL_FOOTER,
    showSignatures: false,
    poNumber: doc.poNumber,
    poDate: doc.poDate,
    buyerName: doc.buyerName,
    buyerAddress: doc.buyerAddress,
    buyerGstin: doc.buyerGstin,
    vendorName: doc.vendorName,
    vendorAddress: doc.vendorAddress,
    vendorGstin: doc.vendorGstin,
    shipTo: doc.shipTo,
    paymentTerms: doc.paymentTerms,
    lines: doc.lines,
    taxableTotal: doc.taxableTotal,
    gstLines: doc.gstLines,
    grandTotal: doc.grandTotal,
    forPdfCapture: pdfCap,
  });

  const annexureHtml = buildPoAnnexureHtml({
    allocations: doc.allocations,
    footerNote: doc.footerNote.trim() || DEFAULT_PO_DIGITAL_FOOTER,
    accentColorHex: doc.accentColorHex,
    forPdfCapture: pdfCap,
  });

  const safe = header.poNumber.replace(/[^\w.-]+/g, "_");
  return { html, annexureHtml, downloadBasename: `PO_${safe}` };
}

export async function openIssuePoPdfInNewTab(
  header: IssuePoHeader,
  items: IssuePoItem[],
  options?: {
    rateByKey?: Record<string, string>;
    hsnByKey?: Record<string, string>;
    applications?: Application[];
  },
): Promise<void> {
  const s = loadPoFormatSettings();
  await warmPoPdfLogoCache([s.logoPrimaryUrl, s.logoSecondaryUrl]);
  const doc = buildIssuePoPdfDocument(header, items, options);
  const { html } = buildIssuePoPdfHtml(header, items, { ...options, forPdfCapture: true });
  const blob = await htmlDocumentToPdfBlob(html, {
    annexure: {
      allocations: doc.allocations,
      accentColorHex: doc.accentColorHex,
      footerNote: doc.footerNote.trim() || DEFAULT_PO_DIGITAL_FOOTER,
    },
  });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    URL.revokeObjectURL(url);
    throw new Error("Popup blocked — allow pop-ups for this site to view the PDF.");
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}

export async function downloadIssuePoPdf(
  header: IssuePoHeader,
  items: IssuePoItem[],
  options?: {
    rateByKey?: Record<string, string>;
    hsnByKey?: Record<string, string>;
    applications?: Application[];
  },
): Promise<void> {
  const s = loadPoFormatSettings();
  await warmPoPdfLogoCache([s.logoPrimaryUrl, s.logoSecondaryUrl]);
  const doc = buildIssuePoPdfDocument(header, items, options);
  const { html, downloadBasename } = buildIssuePoPdfHtml(header, items, {
    ...options,
    forPdfCapture: true,
  });
  await downloadPdfFromHtml(html, downloadBasename, {
    annexure: {
      allocations: doc.allocations,
      accentColorHex: doc.accentColorHex,
      footerNote: doc.footerNote.trim() || DEFAULT_PO_DIGITAL_FOOTER,
    },
  });
}
